<template>
  <ModalDialog v-model="showModal" :closeOnEsc="false" :showXCloseButton="true" size="md">
    <div v-if="memberCardNumber && membershipCardModal?.fields" class="flex flex-col mt-6">
      <!--Membership Card-->
      <MembershipCard
        :variant="CardVariant.BACK"
        :cardTitle="membershipCardModal?.fields?.cardTitle?.value"
        :joinDateLabel="membershipCardModal?.fields?.joinDateLabel?.value"
      />
      <div class="flex flex-col gap-6">
        <!--Barcode Info Text-->
        <div
          class="text-sm text-racq-med-grey text-center mt-3"
          v-html="membershipCardModal?.fields.barcodeInfoText?.value"
        ></div>

        <!--Add to Wallet (mobile only)-->
        <!--Show button only on mobile and for supported platforms-->
        <!-- As per PSR-1697, hide button for 'unsupported' AND 'android' devices as well -->
        <div v-if="isAppleWalletEnabled && platform === 'apple'" class="m-auto flex md:hidden">
          <button type="button" :aria-label="wallet.apple.alt" @click="() => handleWalletClick('apple')">
            <img :src="wallet.apple.icon" :alt="wallet.apple.alt" class="w-[100%]" />
          </button>
        </div>
        <div
          v-if="isGoogleWalletEnabled && (platform === 'apple' || platform === 'android')"
          class="m-auto flex md:hidden"
        >
          <button type="button" :aria-label="wallet.android.alt" @click="() => handleWalletClick('android')">
            <img :src="wallet.android.icon" :alt="wallet.android.alt" class="w-[100%]" />
          </button>
        </div>

        <!--Card Number Box-->
        <div class="bg-racq-anti-flash-white rounded-md px-4 py-5">
          <div class="text-m-bold text-center text-racq-dark-grey">
            {{ membershipCardModal?.fields.memberCardNumberLabel?.value }}
          </div>
          <div class="flex items-center justify-center text-base text-dark-grey-text">
            <span class="mr-2">{{ memberCardNumber.replaceAll(' ', '') }}</span>
            <button
              type="button"
              class="text-m-link font-medium text-racq-blue underline"
              :aria-live="'polite'"
              @click.stop.prevent="copyToClipboard"
            >
              {{
                isCopied
                  ? membershipCardModal?.fields.copiedCardLabel?.value
                  : membershipCardModal?.fields.copyCardLabel?.value
              }}<img
                :src="buttonIcon"
                :alt="isCopied ? 'success' : 'copied'"
                role="img"
                :class="`inline-block ml-1 ${isCopied ? 'h-4 w-4' : 'h-[18px] w-[18px]'}`"
              />
            </button>
          </div>
        </div>
        <!--CTAs-->
        <div class="flex flex-col gap-4 text-center">
          <button class="btn btn-secondary btn-md" @click="orderPhysicalCard">
            {{ membershipCardModal?.fields.manageCardCta?.value.text }}
          </button>
          <a
            :href="membershipCardModal?.fields?.viewCardSettings?.fields?.link?.value?.href"
            class="cursor-pointer font-sans"
          >
            {{ membershipCardModal?.fields?.viewCardSettings?.fields?.link?.value?.text }}
          </a>
          <a
            v-if="platform === 'unsupported'"
            :href="membershipCardModal?.fields?.howToAddWallet?.fields?.link?.value?.href"
            class="cursor-pointer font-sans"
          >
            {{ membershipCardModal?.fields?.howToAddWallet?.fields?.link?.value?.text }}
          </a>
          <button
            v-if="platform == 'unsupported' && membershipCardModal?.fields.walletHelpCta?.value?.href"
            aria-label="Open Wallet Help"
            class="text-racq-blue underline text-base-bold"
            @click="openWalletHelp"
          ></button>
        </div>
      </div>
    </div>
    <div v-else class="text-center py-10 text-gray-500">{{ membershipCardModal.loadingText?.value }}</div>
  </ModalDialog>
  <OrderPhysicalCardModal
    v-show="memberCardNumber"
    ref="orderPhysicalCardModal"
    :membershipCardModal="membershipCardModal"
  />
</template>
<script setup lang="ts">
import { ref, computed, defineExpose, onMounted } from 'vue';
import ModalDialog from '@/components/ModalDialog/ModalDialog.vue';
import MembershipCard from '@/components/MembershipCard/MembershipCard.vue';
import { useAccountStore } from '@/store';
import { downloadGoogleMembershipCard, downloadAppleMembershipCard } from '@/constants/forms-api.const';
import { Field } from '@sitecore-jss/sitecore-jss-vue';
import { LinkFieldValue } from '@sitecore-jss/sitecore-jss-vue/types/components/Link';
import { toFormattedCardNumber } from '@/utils/convert';
import { CardVariant } from '@/enums/membership-card-variant.enum';
import { HOME_PAGE } from '@/constants/urls.const';
import OrderPhysicalCardModal from './OrderPhysicalCardModal.vue';
import { useFeatureTogglesStore } from '@/store/feature-toggles/featureTogglesStore';
import { Link } from '@sitecore-jss/sitecore-jss-vue';

// ---- Types ----
interface MembershipCardModalFields {
  // content / copy
  cardTitle?: Field<string>;
  joinDateLabel?: Field<string>;
  barcodeInfoText?: Field<string>;
  memberCardNumberLabel?: Field<string>;
  copyCardLabel?: Field<string>;
  copiedCardLabel?: Field<string>;
  walletHelpLabel?: Field<string>;
  loadingText?: Field<string>;

  // links
  walletHelpUrl?: string;
  walletHelpCta?: Field<LinkFieldValue>;
  manageCardCta?: Field<LinkFieldValue>;

  // assets
  successIcon?: string;
  copyIcon?: string;

  // temp solution for order membership card PSR-1250
  orderNewCardText?: Field<string>;
  orderNewCardBtn?: Field<string>;
  expectedArrivalText?: Field<string>;
  expectedArrivalBtn?: Field<string>;

  successText?: Field<string>;
  alreadyOrderedTitle?: Field<string>;
  alreadyOrderedText?: Field<string>;
  errorText?: Field<string>;
}

interface Props {
  /** v-model visibility */
  closeOnEsc?: boolean;
  membershipCardModal: any; //MembershipCardModalFields
}

const props = withDefaults(defineProps<Props>(), {
  closeOnEsc: true,
  membershipCardModal: () => ({
    cardTitle: {
      value: 'MEMBERSHIP CARD No.',
    },
    joinDateLabel: {
      value: 'JOIN DATE',
    },
    barcodeInfoText: {
      value: 'Scan this barcode to save at selected RACQ <br> Show & Save retailers and fuel partners.',
    },
    memberCardNumberLabel: { value: 'Membership card number' },
    copyCardLabel: { value: 'Copy' },
    copiedCardLabel: { value: 'Copied' },
    walletHelpLabel: { value: 'How can I add this card to my wallet?' },
    loadingText: { value: 'Loading card details...' },
    walletHelpUrl: HOME_PAGE,
    walletHelpCta: {
      value: {
        href: HOME_PAGE,
      },
    },
    manageCardCta: {
      value: {
        text: 'Manage membership card',
        href: HOME_PAGE,
      },
    },
  }),
});

const accountStore = useAccountStore();
const featureToggleStore = useFeatureTogglesStore();
const showModal = ref(false);
const showPhysicalCardModal = ref(false);
const buttonText = ref<string>('Copy');
const isCopied = ref(false);
const isGoogleWalletEnabled = ref(false);
const isAppleWalletEnabled = ref(false);
const memberCardNumber = computed(() => accountStore.identityDetails?.membershipCardNumber || '');

const orderPhysicalCardModal = ref<InstanceType<typeof OrderPhysicalCardModal>>();

const buttonIcon = computed(() =>
  isCopied.value
    ? new URL('@/assets/icons/green-success-icon.svg', import.meta.url).href
    : new URL('@/assets/icons/copy-icon.svg', import.meta.url).href
);

const platform = computed(() => {
  const ua = navigator.userAgent || navigator.vendor;
  const isAndroid = /android/i.test(ua);
  const isApple = /iPad|iPhone|iPod/.test(ua) && !(window as any).MSStream;
  return isAndroid ? 'android' : isApple ? 'apple' : 'unsupported';
});

const wallet = computed(() => {
  return {
    apple: {
      icon: new URL('@/assets/icons/apple_wallet.svg', import.meta.url).href,
      alt: 'Add to Apple Wallet',
    },
    android: {
      icon: new URL('@/assets/icons/google_wallet.svg', import.meta.url).href,
      alt: 'Add to Apple Wallet',
    },
  };
});

onMounted(async () => {
  isAppleWalletEnabled.value = await featureToggleStore.isFeatureToggleEnabled('enableMembershipCardAppleWallet');
  isGoogleWalletEnabled.value = await featureToggleStore.isFeatureToggleEnabled('enableMembershipCardGoogleWallet');
});

defineExpose({
  showModal: async () => {
    showModal.value = true;
  },
  showOrderPhysicalCardModal: () => {
    showModal.value = false;
    orderPhysicalCardModal.value?.showModal();
  },
});

async function copyToClipboard() {
  if (memberCardNumber?.value) {
    try {
      await navigator.clipboard.writeText(memberCardNumber.value);
      buttonText.value = 'Copied';
      isCopied.value = true;
      setTimeout(() => {
        buttonText.value = 'Copy';
        isCopied.value = false;
      }, 2000);
    } catch (err) {
      console.error('Failed to copy: ', err);
    }
  }
}

function handleWalletClick(walletPlatform: 'apple' | 'android') {
  if (walletPlatform === 'android') {
    window.location.href = downloadGoogleMembershipCard;
  } else if (walletPlatform === 'apple') {
    alert('Redirecting to Apple Wallet');
    window.location.href = downloadAppleMembershipCard;
  } else {
    alert('Wallet not supported on this device.');
    //send email
  }
}

function openWalletHelp() {
  if (props.membershipCardModal.walletHelpCta?.value?.href)
    window.open(props.membershipCardModal.walletHelpCta?.value?.href, '_blank');
}

/**
 * Temp solution for 26.3-digital ONLY
 * https://jira.racqgroup.local/browse/PSR-1250
 */
function orderPhysicalCard() {
  showModal.value = false;
  showPhysicalCardModal.value = true;
  orderPhysicalCardModal.value?.showModal();
}
</script>
